/**
 * 发品前为自定义颜色调用 bg.goods.spec.create，拿到可用的 specId。
 * Temu addCustomAttributeValue 为 stub，必须走 createPublishSpec。
 */
import {
  isBlankTemuAttributeValueId,
  resolveSkcColorAgainstTemplate,
} from '@/temu-platform/utils/temuAttributeTemplate';

function pickColorParentSpecId(colorAttr, attrTpl) {
  const fromAttr = Number(
    colorAttr?.temu_prop?.parentSpecId
    ?? colorAttr?.parentSpecId
    ?? 0,
  );
  if (fromAttr > 0) return fromAttr;
  const rawInfos = attrTpl?.raw?.data?.[0]?.attribute_infos
    || attrTpl?.raw?.attribute_infos
    || [];
  const id = String(colorAttr?.attributeId || '').trim();
  const raw = (Array.isArray(rawInfos) ? rawInfos : []).find(
    (r) => String(r?.attribute_id ?? r?.attributeId) === id,
  );
  return Number(raw?.temu_prop?.parentSpecId || 0) || 0;
}

/**
 * @returns {Promise<object[]>} 写回 colorValueId / temuSpecId 后的 skcList
 */
export async function ensureTemuCustomColorSpecs({
  shopId,
  skcList = [],
  colorAttr = null,
  attrTpl = null,
  api = null,
} = {}) {
  if (!shopId || !api || !colorAttr) {
    return Array.isArray(skcList) ? skcList : [];
  }
  const parentSpecId = pickColorParentSpecId(colorAttr, attrTpl);
  const next = [];
  for (const skc of (Array.isArray(skcList) ? skcList : [])) {
    const resolved = resolveSkcColorAgainstTemplate(skc, colorAttr);
    if (!isBlankTemuAttributeValueId(resolved.colorValueId)) {
      if (!isBlankTemuAttributeValueId(skc?.colorValueId)) {
        // 原本就带平台 value id：原样返回，避免无谓改写
        next.push(skc);
        continue;
      }
      // 颜色名命中类目模板已有值：平台 value id 只存在于 resolved 上。
      // 这里必须推 resolved，否则原 skc 的 colorValueId 仍为空，组包会被判「缺少平台规格 ID」；
      // temuSpecId 清空后由组包按模板 rawVal.temu_spec_id / attribute_value_id 重新取。
      next.push({
        ...resolved,
        temuSpecId: '',
      });
      continue;
    }
    const name = String(resolved.colorExtraValue || resolved.colorLabel || '').trim();
    if (!name) {
      throw new Error('自定义颜色名称不能为空，请重新选择颜色');
    }
    if (!(parentSpecId > 0)) {
      throw new Error('缺少颜色父规格 ID，请刷新类目属性模板后重试');
    }
    const res = await api.createPublishSpec(shopId, {
      parentSpecId,
      specName: name,
    });
    const specId = Number(res?.specId || 0);
    if (!(specId > 0)) {
      throw new Error(`创建颜色规格「${name}」失败：未返回 specId`);
    }
    next.push({
      ...skc,
      colorValueId: String(specId),
      temuSpecId: specId,
      colorIsCustom: true,
      colorExtraValue: name,
      colorLabel: name,
    });
  }
  return next;
}
